/**
 * Table generator — a rectangular table built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width (left–right)
 *   - Y = depth (front–back)
 *   - Z = height
 *
 * Default dimensions approximate a standard dining table (1.4 m × 0.8 m ×
 * 0.74 m). Legs, apron, and top are all separate BoxSpecs, letting the user
 * tune each independently. Three leg styles are supported to complement the
 * Chair generator's own leg styles:
 *
 *   - four_leg:  One round-ish leg at each corner (approximated as a box).
 *   - trestle:   Two A-frame trestles, one near each end.
 *   - pedestal:  A single central rectangular column.
 */

import type { BoxSpec } from "../generators/generatorGeom.ts";
import { concatBoxes } from "../generators/generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface TableParams extends GeneratorParams {
  width: number;
  depth: number;
  height: number;
  topThickness: number;
  hasApron: boolean;
  apronHeight: number;
  apronThickness: number;
  legStyle: string;
  legSize: number;
  legInset: number;
}

const TABLE_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "table-top",
    label: "Top",
    params: [
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 1.4,
        min: 0.4,
        max: 4,
        step: 0.05,
      },
      {
        id: "depth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.8,
        min: 0.4,
        max: 2,
        step: 0.05,
      },
      {
        id: "height",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.74,
        min: 0.5,
        max: 1.1,
        step: 0.01,
      },
      {
        id: "topThickness",
        label: "Top Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.1,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "table-apron",
    label: "Apron",
    defaultCollapsed: true,
    params: [
      { id: "hasApron", label: "Apron", type: "boolean", default: true },
      {
        id: "apronHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.06,
        min: 0.02,
        max: 0.2,
        step: 0.005,
        showIf: { id: "hasApron", value: true },
      },
      {
        id: "apronThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.02,
        min: 0.01,
        max: 0.05,
        step: 0.005,
        showIf: { id: "hasApron", value: true },
      },
    ],
  },
  {
    type: "section",
    id: "table-legs",
    label: "Legs",
    params: [
      {
        id: "legStyle",
        label: "Style",
        type: "enum",
        default: "four_leg",
        options: [
          { value: "four_leg", label: "Four Leg" },
          { value: "trestle", label: "Trestle" },
          { value: "pedestal", label: "Pedestal" },
        ],
      },
      {
        id: "legSize",
        label: "Leg Size",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.02,
        max: 0.15,
        step: 0.005,
      },
      {
        id: "legInset",
        label: "Inset",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0,
        max: 0.2,
        step: 0.005,
        showIf: { id: "legStyle", value: "four_leg" },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

const LEG_STYLES = ["four_leg", "trestle", "pedestal"] as const;

function buildTable(
  p: TableParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const width = numParam(p, "width", 1.4);
  const depth = numParam(p, "depth", 0.8);
  const height = numParam(p, "height", 0.74);
  const topT = numParam(p, "topThickness", 0.04);
  const hasApron = boolParam(p, "hasApron", true);
  const apronH = numParam(p, "apronHeight", 0.08);
  const apronT = numParam(p, "apronThickness", 0.02);
  const rawStyle = enumParam(p, "legStyle", "four_leg");
  const legStyle = (LEG_STYLES as readonly string[]).includes(rawStyle)
    ? rawStyle
    : "four_leg";
  const legSize = numParam(p, "legSize", 0.05);
  const legInset = numParam(p, "legInset", 0.05);

  const boxes: BoxSpec[] = [];

  // Table top — centred at origin, top face at z = height
  boxes.push({
    cx: 0,
    cy: 0,
    cz: height - topT * 0.5,
    sx: width,
    sy: depth,
    sz: topT,
  });

  const apronTopZ = height - topT;

  // Apron — four rails just below the top surface, set in by apronT from
  // the outer edge of the table top.
  if (hasApron) {
    const apronCZ = apronTopZ - apronH * 0.5;
    const innerW = width - apronT * 2;
    // Front and back apron rails (span innerW so they fit between side rails)
    boxes.push({
      cx: 0,
      cy: depth * 0.5 - apronT * 0.5,
      cz: apronCZ,
      sx: innerW,
      sy: apronT,
      sz: apronH,
    });
    boxes.push({
      cx: 0,
      cy: -depth * 0.5 + apronT * 0.5,
      cz: apronCZ,
      sx: innerW,
      sy: apronT,
      sz: apronH,
    });
    // Left and right apron rails (full depth so corners are solid)
    boxes.push({
      cx: width * 0.5 - apronT * 0.5,
      cy: 0,
      cz: apronCZ,
      sx: apronT,
      sy: depth,
      sz: apronH,
    });
    boxes.push({
      cx: -width * 0.5 + apronT * 0.5,
      cy: 0,
      cz: apronCZ,
      sx: apronT,
      sy: depth,
      sz: apronH,
    });
  }

  // Legs — height is from floor (z=0) to underside of apron (or underside of top)
  const legTopZ = hasApron ? apronTopZ - apronH : apronTopZ;
  const legH = legTopZ;
  const legCZ = legH * 0.5;

  if (legStyle === "four_leg") {
    const xOff = width * 0.5 - legInset - legSize * 0.5;
    const yOff = depth * 0.5 - legInset - legSize * 0.5;
    for (const sx of [1, -1])
      for (const sy of [1, -1]) {
        boxes.push({
          cx: sx * xOff,
          cy: sy * yOff,
          cz: legCZ,
          sx: legSize,
          sy: legSize,
          sz: legH,
        });
      }
  } else if (legStyle === "trestle") {
    // Two trestles — each is a wide cross-brace near each short end.
    // Trestle width spans the full table depth, thickness = legSize.
    const tXOff = width * 0.5 - legSize * 2;
    for (const sx of [1, -1]) {
      // Vertical trestle post
      boxes.push({
        cx: sx * tXOff,
        cy: 0,
        cz: legCZ,
        sx: legSize,
        sy: depth * 0.6,
        sz: legH,
      });
    }
    // Horizontal stretcher connecting the trestles near floor level
    boxes.push({
      cx: 0,
      cy: 0,
      cz: legSize * 0.5,
      sx: width - legSize * 4,
      sy: legSize,
      sz: legSize,
    });
  } else {
    // Pedestal — single central column
    const pedW = legSize * 2.5;
    boxes.push({ cx: 0, cy: 0, cz: legCZ, sx: pedW, sy: pedW, sz: legH });
    // Foot base
    boxes.push({
      cx: 0,
      cy: 0,
      cz: legSize * 0.25,
      sx: width * 0.5,
      sy: depth * 0.5,
      sz: legSize * 0.5,
    });
  }

  return { kind: "mesh", geometry: concatBoxes(boxes) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const TABLE_GENERATOR: Generator = defineGenerator<TableParams>({
  id: "table",
  label: "Table",
  category: "prop",
  paramSpecs: TABLE_SPECS,
  defaultParams: paramsFromSpecs(TABLE_SPECS) as TableParams,
  build: buildTable,
});
